import type {
  ChecklistState,
  ProjectSummary,
  PublicUser,
  RunSummary,
  SessionResponse,
  TaskDetail,
  TaskPlatform,
  TaskSummary,
} from '../shared/api';

const tokenKey = 'test-platform.token';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return window.localStorage.getItem(tokenKey);
}

export function setToken(token: string | null): void {
  if (token) {
    window.localStorage.setItem(tokenKey, token);
    document.cookie = `test-platform-token=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
  } else {
    window.localStorage.removeItem(tokenKey);
    document.cookie = 'test-platform-token=; path=/; SameSite=Lax; max-age=0';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  const response = await fetch(path, { ...init, headers });
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
  } & T;
  if (!response.ok) {
    throw new ApiError(
      body.error || `Request failed (${response.status})`,
      response.status,
    );
  }
  return body;
}

export const api = {
  login: (username: string, password: string) =>
    request<SessionResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  me: () => request<{ user: PublicUser }>('/api/auth/me'),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  users: () => request<{ users: PublicUser[] }>('/api/users'),
  createUser: (input: {
    username: string;
    password: string;
    role: PublicUser['role'];
  }) =>
    request<{ user: PublicUser }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  projects: () => request<{ projects: ProjectSummary[] }>('/api/projects'),
  deleteProject: (projectId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}`, { method: 'DELETE' }),
  createProject: (name: string) =>
    request<{ project: ProjectSummary }>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  members: (projectId: string) =>
    request<{ members: PublicUser[] }>(`/api/projects/${projectId}/members`),
  addMember: (projectId: string, userId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  tasks: (projectId: string) =>
    request<{ tasks: TaskSummary[] }>(`/api/projects/${projectId}/tasks`),
  createTask: (
    projectId: string,
    input: {
      title: string;
      platform: TaskPlatform;
      text?: string;
      docxBase64?: string;
    },
  ) =>
    request<{ task: TaskSummary }>(`/api/projects/${projectId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  task: (taskId: string) =>
    request<{ task: TaskDetail }>(`/api/tasks/${taskId}`),
  deleteTask: (taskId: string) =>
    request<{ ok: true }>(`/api/tasks/${taskId}`, { method: 'DELETE' }),
  updateTask: (
    taskId: string,
    input: {
      title?: string;
      platform?: TaskPlatform;
      text?: string;
      docxBase64?: string;
      resetChecklist?: boolean;
      expectedChecklistRevision?: number;
    },
  ) =>
    request<{ task: TaskDetail }>(`/api/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  updateChecklist: (
    taskId: string,
    input: {
      checklist: NonNullable<ChecklistState['checklist']>;
      expectedChecklistRevision: number;
    },
  ) =>
    request<ChecklistState>(`/api/tasks/${taskId}/checklist`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  run: (runId: string) => request<{ run: RunSummary }>(`/api/runs/${runId}`),
};

export const reportUrl = (runId: string): string =>
  `/api/runs/${runId}/report?token=${encodeURIComponent(getToken() ?? '')}`;
